import type { Chip } from "../core/chip";
import type { ChipStore } from "./ChipStore";
import type { ApplyDeltaResult } from "./ChipStore";
import { applyDelta, canApplyDelta, type ConflictRecord } from "../core/invoke";

const DB_NAME = "deckos";
const DB_VERSION = 2;
const STORE = "chips";

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE))
        db.createObjectStore(STORE, { keyPath: "id" });
      if (!db.objectStoreNames.contains("snapshots"))
        db.createObjectStore("snapshots", { keyPath: "id" });
      if (!db.objectStoreNames.contains("deltas"))
        db.createObjectStore("deltas", { keyPath: "id" });
      if (!db.objectStoreNames.contains("conflicts"))
        db.createObjectStore("conflicts", { keyPath: "id" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export const indexedDbStore: ChipStore = {
  async list() {
    const db = await openDb();
    return await new Promise<Chip[]>((resolve, reject) => {
      const request = db
        .transaction(STORE, "readonly")
        .objectStore(STORE)
        .getAll();
      request.onsuccess = () => {
        const result = request.result as Chip[];
        db.close();
        resolve(result);
      };
      request.onerror = () => reject(request.error);
    });
  },
  async get(id) {
    const db = await openDb();
    return await new Promise<Chip | undefined>((resolve, reject) => {
      const request = db
        .transaction(STORE, "readonly")
        .objectStore(STORE)
        .get(id);
      request.onsuccess = () => {
        const result = request.result as Chip | undefined;
        db.close();
        resolve(result);
      };
      request.onerror = () => reject(request.error);
    });
  },
  async put(chip) {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const request = db
        .transaction(STORE, "readwrite")
        .objectStore(STORE)
        .put(chip);
      request.onsuccess = () => {
        db.close();
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  },
  async applyDeltaAtomic(chip, delta): Promise<ApplyDeltaResult> {
    const db = await openDb();
    const transaction = db.transaction(
      ["chips", "snapshots", "deltas", "conflicts"],
      "readwrite",
    );
    const chips = transaction.objectStore("chips");
    const snapshots = transaction.objectStore("snapshots");
    const deltas = transaction.objectStore("deltas");
    const conflicts = transaction.objectStore("conflicts");
    return await new Promise<ApplyDeltaResult>((resolve) => {
      const request = chips.get(chip.id);
      request.onerror = () => {
        transaction.abort();
        db.close();
        resolve({
          status: "FAILED",
          reason: "Unable to read current CHIP.",
          recoverable: true,
        });
      };
      request.onsuccess = () => {
        const current = request.result as Chip | undefined;
        if (
          !current ||
          delta.chipId !== (current?.id ?? chip.id) ||
          delta.baseRevision !== (current?.revision ?? chip.revision)
        ) {
          const conflict: ConflictRecord = {
            id: `conflict-${Date.now()}`,
            delta,
            currentRevision: current?.revision ?? chip.revision,
          };
          conflicts.put(conflict);
          transaction.oncomplete = () => {
            db.close();
            resolve({ status: "STALE", conflict });
          };
          return;
        }
        if (!canApplyDelta(current, delta)) {
          transaction.abort();
          db.close();
          resolve({
            status: "REJECTED",
            reason: "DELTA revision target is invalid.",
          });
          return;
        }
        const next = applyDelta(current, delta);
        if (!next || next.revision !== delta.nextRevision) {
          transaction.abort();
          db.close();
          resolve({ status: "REJECTED", reason: "DELTA validation failed." });
          return;
        }
        const deltaId = `delta-${delta.chipId}-${delta.nextRevision}`;
        snapshots.put({
          id: `${current.id}-${current.revision}`,
          chip: current,
        });
        chips.put(next);
        deltas.put({ id: deltaId, delta, status: "APPLIED" });
        transaction.oncomplete = () => {
          db.close();
          resolve({ status: "APPLIED", chip: next, appliedDeltaId: deltaId });
        };
        transaction.onerror = () => {
          db.close();
          resolve({
            status: "FAILED",
            reason: "Atomic DELTA transaction failed.",
            recoverable: true,
          });
        };
      };
    });
  },
};
